import {
  AndroidRounded,
  ArrowOutwardRounded,
  AutoAwesome,
  CloudRounded,
  DesktopWindows,
  ElectricBoltRounded,
  WebAssetRounded,
} from "@mui/icons-material"
import { Button, Divider, Paper, Stack, Typography } from "@mui/material"
import { useQuery } from "@tanstack/react-query"
import React, { useMemo } from "react"
import { Footer } from "src/components/Footer"
import { StaggeredList } from "src/components/StaggeredList"
import { TitleFont } from "src/theme"
import {
  getIconComponent,
  getPlatformDownloads,
  selectPlatformDownload,
} from "src/utils/github-api"
import { SPRING_CONFIGS } from "src/utils/utils"

import { Subheading } from "../components/Subheading"
import { UnderTitle } from "../components/UnderTitle"

export default function PricingPage() {
  const { data: allDownloads } = useQuery({
    queryFn: getPlatformDownloads,
    queryKey: ["platformDownloads"],
  })

  const download = useMemo(() => {
    if (!allDownloads || allDownloads.length === 0) return null
    return selectPlatformDownload(allDownloads)
  }, [allDownloads])

  return (
    <>
      <Stack gap={6} flexGrow={1}>
        <StaggeredList gap={2} alignItems="center" config={SPRING_CONFIGS.slow} delay={600}>
          <Subheading>Pricing</Subheading>
          <UnderTitle>Simple and transparent pricing</UnderTitle>
        </StaggeredList>
        <StaggeredList
          secondary
          delay={200}
          config={SPRING_CONFIGS.quick}
          direction={{ md: "row", xs: "column" }}
          gap={4}
          justifyContent="center"
        >
          <Paper
            elevation={0}
            sx={{
              // backdropFilter: "blur(10px)",
              background: "rgba(255, 255, 255, 0.33)",
              borderRadius: 2,
              borderWidth: "2px !important",
              "html.dark &": {
                background: "rgba(255, 255, 255, 0.05)",
              },
              padding: 4,
              width: { md: 360, xs: "100%" },
            }}
          >
            <Stack gap={1}>
              <Stack>
                <Typography variant="h6" fontWeight={600} fontFamily={TitleFont}>
                  Hobby
                </Typography>
                <Typography variant="h3" fontWeight={700} fontFamily={TitleFont}>
                  Free
                </Typography>
              </Stack>
              <Divider />
              <Typography variant="subtitle1" fontWeight={600}>
                Includes
              </Typography>
              <Stack gap={2}>
                <Stack direction="row" gap={2} alignItems="center">
                  <CloudRounded fontSize="small" />
                  <Typography>Cloud hosting</Typography>
                </Stack>
                <Stack direction="row" gap={2} alignItems="center">
                  <ElectricBoltRounded fontSize="small" />
                  <Typography>
                    Normal server <br />
                    <Typography component="span" variant="caption" color="text.secondary">
                      1 CPU - 512MB RAM
                    </Typography>
                  </Typography>
                </Stack>
                <Stack direction="row" gap={2} alignItems="center">
                  <DesktopWindows fontSize="small" />
                  <Typography>Desktop apps</Typography>
                </Stack>
                <Stack direction="row" gap={2} alignItems="center">
                  <WebAssetRounded fontSize="small" />
                  <Typography>Web app</Typography>
                </Stack>
                <Stack direction="row" gap={2} alignItems="center">
                  <AndroidRounded fontSize="small" />
                  <Typography>
                    Mobile apps
                    <br />
                    <Typography
                      component="span"
                      variant="caption"
                      color="text.secondary"
                      fontStyle="italic"
                    >
                      Coming soon
                    </Typography>
                  </Typography>
                </Stack>
                <Stack direction="row" gap={2} alignItems="center">
                  <AutoAwesome fontSize="small" />
                  <Typography>
                    AI assistant
                    <br />
                  </Typography>
                </Stack>
              </Stack>
              <Stack direction="row" gap={1}>
                <Button
                  href={download?.url || "https://privatefolio.app"}
                  target="_blank"
                  variant="contained"
                  color="primary"
                  disableElevation
                  sx={{
                    "& svg": {
                      height: "16px !important",
                      marginRight: "0px !important",
                      width: "16px !important",
                    },
                    alignSelf: "flex-start",
                    borderRadius: 4,
                    fontSize: "1rem",
                    fontWeight: 600,
                    gap: 0.5,
                    marginTop: 4,
                    paddingX: 3,
                    paddingY: 1,
                  }}
                  startIcon={
                    download?.icon ? getIconComponent(download.icon, download.iconStyle) : null
                  }
                >
                  {download ? "Download" : "Open web app"}
                  {!download && <ArrowOutwardRounded sx={{ fontSize: "1rem !important" }} />}
                </Button>
                <Button
                  href="/apps"
                  variant="outlined"
                  color="primary"
                  size="large"
                  disableElevation
                  sx={{
                    alignSelf: "flex-start",
                    borderRadius: 4,
                    fontSize: "1rem",
                    fontWeight: 600,
                    marginTop: 4,
                    paddingY: 1,
                  }}
                >
                  Others
                </Button>
              </Stack>
            </Stack>
          </Paper>
          <Paper
            elevation={0}
            sx={{
              // backdropFilter: "blur(10px)",
              background:
                "radial-gradient(130% 100% at 70% 130%, #f7dbff 0%, rgba(255, 255, 255, 0.33) 100%)",
              border: "1px solid rgba(var(--mui-palette-accent-mainChannel) / 0.5)",
              borderRadius: 2,
              borderWidth: "2px !important",
              "html.dark &": {
                background:
                  "radial-gradient(130% 100% at 70% 130%, #7b1cba 0%, rgba(255, 255, 255, 0.05) 100%)",
              },
              padding: 4,
              width: { md: 360, xs: "100%" },
            }}
          >
            <Stack gap={1}>
              <Stack>
                <Typography variant="h6" fontWeight={600} fontFamily={TitleFont}>
                  Premium
                </Typography>
                <Typography variant="h3" fontWeight={700} fontFamily={TitleFont}>
                  $5{" "}
                  <Typography component="span" variant="h6" color="text.secondary">
                    /month
                  </Typography>
                </Typography>
              </Stack>
              <Divider />
              <Typography variant="subtitle1" fontWeight={600}>
                Includes
              </Typography>
              <Stack gap={2}>
                <Stack direction="row" gap={2} alignItems="center">
                  <CloudRounded fontSize="small" />
                  <Typography>Cloud hosting</Typography>
                </Stack>
                <Stack direction="row" gap={2} alignItems="center">
                  <ElectricBoltRounded fontSize="small" />
                  <Typography>
                    Quicker server <br />
                    <Typography component="span" variant="caption" color="text.secondary">
                      2 CPUs - 1GB RAM
                    </Typography>
                  </Typography>
                </Stack>
                <Stack direction="row" gap={2} alignItems="center">
                  <DesktopWindows fontSize="small" />
                  <Typography>Desktop apps</Typography>
                </Stack>
                <Stack direction="row" gap={2} alignItems="center">
                  <WebAssetRounded fontSize="small" />
                  <Typography>Web app</Typography>
                </Stack>
                <Stack direction="row" gap={2} alignItems="center">
                  <AndroidRounded fontSize="small" />
                  <Typography>
                    Mobile apps
                    <br />
                    <Typography
                      component="span"
                      variant="caption"
                      color="text.secondary"
                      fontStyle="italic"
                    >
                      Coming soon
                    </Typography>
                  </Typography>
                </Stack>
                <Stack direction="row" gap={2} alignItems="center">
                  <AutoAwesome fontSize="small" />
                  <Typography>
                    AI assistant
                    <br />
                    <Typography
                      component="span"
                      variant="caption"
                      color="text.secondary"
                      fontStyle="italic"
                    >
                      Newer models, higher limits
                    </Typography>
                  </Typography>
                </Stack>
              </Stack>
              <Button
                href="https://privatefolio.app/cloud"
                target="_blank"
                variant="contained"
                color="primary"
                size="large"
                disableElevation
                sx={{
                  alignSelf: "flex-start",
                  borderRadius: 4,
                  fontSize: "1rem",
                  fontWeight: 600,
                  gap: 0.5,
                  marginTop: 4,
                  paddingY: 1,
                }}
              >
                Sign up
                {/* <ArrowOutwardRounded sx={{ fontSize: "1rem" }} /> */}
              </Button>
            </Stack>
          </Paper>
        </StaggeredList>
      </Stack>
      <Footer />
    </>
  )
}
